/** Exact type recommendation with named stages and no fake percentage. */

import { useEffect, useState } from "react";

import { copy } from "@core/index.js";

import { useAnnounce } from "../app/announcer";
import { useStudio } from "../app/store";
import { applyExactTypeAction, createExactTypeStudio, typeLibrary } from "../wiring/typeLibrary";

type StageState = "pending" | "active" | "done";

export const Processing = ({ onCancelled: _onCancelled }: { onCancelled?: () => void }) => {
  const { state, dispatch } = useStudio();
  const announce = useAnnounce();
  const [stages, setStages] = useState<StageState[]>(["pending", "pending", "pending", "pending"]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      const marks: StageState[] = ["pending", "pending", "pending", "pending"];
      for (let stage = 0; stage < marks.length; stage += 1) {
        marks[stage] = "active";
        if (alive) setStages([...marks]);
        if (stage === 0) {
          try {
            let session = createExactTypeStudio(state.brief);
            const remembered = typeLibrary.fonts.find((font) => font.id === state.rememberedCollectionId);
            if (session && remembered) {
              session = applyExactTypeAction(session, { type: "toggleStarFont", fontId: remembered.id });
              const role = remembered.roles.includes("display") ? "display" : remembered.roles.includes("body") ? "body" : "utility";
              session = applyExactTypeAction(session, { type: "setRoleFont", role, fontId: remembered.id });
            }
            if (!session) throw new Error("No exact type direction survived the brief.");
            if (alive) dispatch({ type: "typeStudioReady", session });
          } catch (cause) {
            if (alive) setError(cause instanceof Error ? cause.message : "The type engine stopped.");
            return;
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 70));
        marks[stage] = "done";
        if (alive) setStages([...marks]);
      }
      if (alive) {
        dispatch({ type: "setStep", step: "direction" });
        announce("Your five type directions are ready.");
      }
    };
    void run();
    return () => { alive = false; };
    // One immutable recommendation run for this committed brief.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div aria-busy={error ? undefined : "true"}>
      <p className="eyebrow">Looking for the voice</p>
      <h2 className="step-title">The type engine is earning its opinion.</h2>
      <p className="step-intro">{copy.processing.noPercent}</p>
      <ol className="stage-list" aria-label="Type engine stages">{copy.processing.type.map((label, index) => <li key={label} data-state={stages[index] ?? "pending"}>{label}</li>)}</ol>
      {error ? <div className="card" role="alert"><h3>The prettier answer lost to an honest constraint.</h3><p>{error}</p><button type="button" className="btn btn--primary" onClick={() => dispatch({ type: "setStep", step: "brief" })}>Change my answers</button></div> : null}
    </div>
  );
};
